import { useState } from 'react';
import useContactStore from '@/stores/ContactStore';
import useUserStore from '@/stores/UserStore';
import { CheckCheckIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn, formatRelativeTime } from '@/lib/utils';
import { transcribeAudioOnDemand } from '@/hooks/contact';
import { AudioPlayer } from '@/components/common/AudioPlayer';

import CustomerReaction from './CustomerReaction';

export default function AudioMessage({
  media_url,
  direction,
  emoji,
  customer_reaction,
  contact_id,
  channel,
  _id: message_id,
  is_seen,
  is_seen_at,
  transcribe,
  isLastSentMessage,
}) {
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { setAudioMessageTranscription } = useContactStore(
    useShallow((state) => ({
      setAudioMessageTranscription: state.setAudioMessageTranscription,
    }))
  );
  const { sentAudioTranscriptionFeatureEnabled } = useUserStore(
    useShallow((state) => ({
      sentAudioTranscriptionFeatureEnabled:
        state.sentAudioTranscriptionFeatureEnabled,
    }))
  );

  const fetchAudioTranscription = async () => {
    setLoading(true);

    toast.promise(transcribeAudioOnDemand(message_id), {
      loading: 'Requesting audio transcription...',
      success: (response) => {
        if (response.status === 'queued') {
          setAudioMessageTranscription(message_id, response.status);
        }
        setLoading(false);
        return 'Audio transcription requested';
      },
      error: (error) => {
        setLoading(false);
        return (
          error.response?.data?.message ||
          'Failed to request audio transcription'
        );
      },
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const shouldShowMore = transcribe?.text && transcribe.text.length > 150;
  const displayText =
    shouldShowMore && !isExpanded
      ? transcribe.text.substring(0, 150) + '...'
      : transcribe?.text;

  // Determine if transcription should be shown based on direction and feature flag
  // always show for received messages and only show for sent messages if feature flag is enabled
  const shouldShowTranscription =
    direction === 'sent' ? sentAudioTranscriptionFeatureEnabled : true;

  return (
    <div className='group relative'>
      <div className='flex min-w-[300px] max-w-[500px] flex-col'>
        <AudioPlayer
          src={media_url}
          foregroundColor={cn(
            direction === 'sent' ? '' : 'text-primary-foreground'
          )}
          backgroundColor={cn(
            direction === 'sent' ? 'bg-muted' : 'bg-received'
          )}
          className={cn(
            direction !== 'sent'
              ? 'rounded-t-lg'
              : shouldShowTranscription
                ? 'rounded-t-lg'
                : 'rounded-lg'
          )}
        />

        {shouldShowTranscription &&
          (transcribe ? (
            transcribe.status === 'done' ? (
              <div
                className={cn(
                  'rounded-b-lg p-2 text-left text-sm',
                  direction === 'sent'
                    ? 'bg-muted'
                    : 'bg-received text-primary-foreground'
                )}
              >
                {displayText}
                {shouldShowMore && (
                  <button
                    onClick={toggleExpanded}
                    className={cn(
                      'ml-1 text-xs underline',
                      direction === 'sent'
                        ? 'text-primary hover:text-primary/80'
                        : 'text-primary-foreground hover:text-primary-foreground/80'
                    )}
                  >
                    {isExpanded ? 'show less' : 'show more'}
                  </button>
                )}
              </div>
            ) : transcribe.status === 'wip' ? (
              <div
                className={cn(
                  'rounded-b-lg p-2 text-left text-sm italic',
                  direction === 'sent'
                    ? 'bg-muted'
                    : 'bg-received text-primary-foreground'
                )}
              >
                Transcription is being processed...
              </div>
            ) : transcribe.status === 'queued' ? (
              <div
                className={cn(
                  'rounded-b-lg p-2 text-left text-sm italic',
                  direction === 'sent'
                    ? 'bg-muted'
                    : 'bg-received text-primary-foreground'
                )}
              >
                Transcription is queued.
              </div>
            ) : transcribe.status === 'error' ? (
              <div
                className={cn(
                  'flex flex-row items-center gap-2 rounded-b-lg p-2 text-left text-sm italic',
                  direction === 'sent'
                    ? 'bg-muted'
                    : 'bg-received text-primary-foreground'
                )}
              >
                <span>Transcription failed. Try again</span>
                {loading ? null : (
                  <RefreshCw
                    className='h-4 w-4 cursor-pointer hover:text-black/50'
                    onClick={fetchAudioTranscription}
                  />
                )}
              </div>
            ) : null
          ) : (
            <div
              className={cn(
                'flex flex-row items-center gap-2 rounded-b-lg p-2 text-left text-sm italic',
                direction === 'sent'
                  ? 'bg-muted'
                  : 'bg-received text-primary-foreground'
              )}
            >
              <div
                className='cursor-pointer italic'
                onClick={fetchAudioTranscription}
              >
                Click to transcribe audio
              </div>
            </div>
          ))}
      </div>
      {is_seen && (
        <span className={'absolute -bottom-2 right-0'}>
          <CheckCheckIcon className='h-4 w-4 text-seen' />
        </span>
      )}
      {(emoji || customer_reaction) && (
        <span
          className={cn(
            'absolute -bottom-3 rounded-full border bg-primary-foreground px-[3px] text-xs text-primary',
            direction !== 'sent' ? 'left-2' : is_seen ? 'right-5' : 'right-2'
          )}
        >
          {emoji}
          {customer_reaction ? (emoji === '❤' ? null : '❤') : null}
          {emoji && customer_reaction ? '2' : null}
        </span>
      )}
      {is_seen_at && isLastSentMessage && direction === 'sent' && (
        <div
          className={cn(
            'absolute -bottom-6 truncate text-xs text-muted-foreground',
            direction === 'sent' ? 'right-0' : 'left-0'
          )}
        >
          Seen {formatRelativeTime(new Date(is_seen_at))}
        </div>
      )}
      {direction !== 'sent' && channel === 'instagram' && (
        <CustomerReaction
          direction={direction}
          customer_reaction={customer_reaction}
          message_id={message_id}
          contact_id={contact_id}
        />
      )}
    </div>
  );
}
